import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../api/client";
import { qk } from "../api/queries";
import type { EnvLogLine, EnvPhase, MonitorEnv, MonitorRun } from "../api/types";
import { Icon } from "../components/Icon";
import { Pill, StatusPill } from "../components/Pill";
import { RelativeTime } from "../components/RelativeTime";
import { PageLoading } from "../components/Spinner";
import { useCurrentUser } from "../lib/auth";
import { cx, formatUsd } from "../lib/format";
import type { PillColor } from "../lib/status";

const AGENT_NAME: Record<string, string> = { claude_code: "Claude Code", codex: "Codex", mock: "Mock agent" };
const agentName = (t: string) => AGENT_NAME[t] ?? t;
const KIND_LABEL: Record<string, string> = {
  work: "Work",
  review: "AI review",
  cross_task_review: "Cross-task review",
  drafting: "Drafting",
};
const BACKEND_LABEL: Record<string, string> = { aws_ec2: "EC2", aws_ecs: "ECS Fargate", docker: "Docker", local: "Local" };

const PHASE_META: Record<EnvPhase, { label: string; color: PillColor }> = {
  provisioning: { label: "Provisioning", color: "yellow" },
  booting: { label: "Booting", color: "yellow" },
  preparing: { label: "Preparing", color: "blue" },
  setup_failed: { label: "Setup failed", color: "red" },
  ready: { label: "Ready", color: "green" },
  working: { label: "Agent working", color: "blue" },
  stopped: { label: "Stopped", color: "gray" },
  failed: { label: "Failed", color: "red" },
  terminated: { label: "Terminated", color: "default" },
};

const STEPS = ["Provision", "Boot", "Connect", "Workspace", "Ready"];
/** Index of the step in progress (STEPS.length = all done), or null when off the happy path. */
function stepIndex(e: MonitorEnv): number | null {
  switch (e.phase) {
    case "provisioning":
      return 0;
    case "booting":
      return e.last_seen_at ? 2 : 1;
    case "preparing":
    case "setup_failed":
      return 3;
    case "ready":
    case "working":
      return STEPS.length;
    default:
      return null;
  }
}

/** Re-render every second (elapsed timers). */
function useSecondTick(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

function elapsed(fromIso: string | null | undefined, now: number): string {
  if (!fromIso) return "";
  const s = Math.max(0, Math.floor((now - Date.parse(fromIso)) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

export function MonitorPage() {
  const [params, setParams] = useSearchParams();
  const recent = params.get("ended") === "1";
  const envs = useQuery({
    queryKey: qk.monitorEnvs(recent),
    queryFn: () => api.monitorEnvironments(recent),
    refetchInterval: 5_000,
    placeholderData: keepPreviousData,
  });
  const runs = useQuery({ queryKey: qk.monitorRuns, queryFn: api.monitorRuns, refetchInterval: 5_000 });
  const now = useSecondTick();

  const list = envs.data ?? [];
  const selectedId = params.get("env") ?? undefined;
  const selected = list.find((e) => e.id === selectedId);
  // Default selection: the environment furthest from ready (most likely to need a look).
  useEffect(() => {
    if (selectedId || list.length === 0) return;
    const pick = list.find((e) => e.phase === "failed" || e.phase === "setup_failed") ?? list.find((e) => stepIndex(e) !== null && stepIndex(e)! < STEPS.length) ?? list[0];
    setParams((p) => {
      p.set("env", pick.id);
      return p;
    }, { replace: true });
  }, [selectedId, list, setParams]);

  const select = (id: string) =>
    setParams((p) => {
      p.set("env", id);
      return p;
    });
  const toggleEnded = () =>
    setParams((p) => {
      if (recent) p.delete("ended");
      else p.set("ended", "1");
      return p;
    });

  if (envs.isLoading || runs.isLoading) return <PageLoading />;

  const activeEnvs = list.filter((e) => !["terminated", "failed"].includes(e.phase));
  const spinningUp = activeEnvs.filter((e) => ["provisioning", "booting", "preparing"].includes(e.phase)).length;
  const problems = list.filter((e) => e.phase === "failed" || e.phase === "setup_failed").length;
  const working = (runs.data ?? []).filter((r) => r.status !== "queued").length;
  const queued = (runs.data ?? []).length - working;

  return (
    <div className="page monitor-page">
      <div className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">Monitor</h1>
          <p className="page-subtitle muted">
            {working} agent{working === 1 ? "" : "s"} working
            {queued > 0 && <>, {queued} queued</>} · {activeEnvs.length} environment{activeEnvs.length === 1 ? "" : "s"}
            {spinningUp > 0 && <>, {spinningUp} starting up</>}
            {problems > 0 && <span className="error-text">, {problems} failed</span>}
          </p>
        </div>
        <label className="monitor-toggle">
          <input type="checkbox" checked={recent} onChange={toggleEnded} /> Show ended (last 24 h)
        </label>
      </div>

      <section className="monitor-section">
        <h2 className="monitor-heading">
          <Icon name="bot" /> Agents
        </h2>
        <AgentsTable runs={runs.data ?? []} envs={list} now={now} onSelectEnv={select} />
      </section>

      <section className="monitor-section">
        <h2 className="monitor-heading">
          <Icon name="server" /> Environments
        </h2>
        {list.length === 0 ? (
          <p className="muted monitor-empty">No environments{recent ? " in the last 24 hours" : " are running"}.</p>
        ) : (
          <div className="monitor-envs">
            <div className="monitor-env-list">
              {list.map((e) => (
                <EnvCard key={e.id} env={e} now={now} selected={e.id === selected?.id} onSelect={() => select(e.id)} />
              ))}
            </div>
            {selected ? <EnvDetail env={selected} now={now} /> : <div className="monitor-log-panel muted monitor-empty">Select an environment to see its log.</div>}
          </div>
        )}
      </section>
    </div>
  );
}

function AgentsTable({
  runs,
  envs,
  now,
  onSelectEnv,
}: {
  runs: MonitorRun[];
  envs: MonitorEnv[];
  now: number;
  onSelectEnv: (id: string) => void;
}) {
  if (runs.length === 0) return <p className="muted monitor-empty">No agents are running.</p>;
  return (
    <div className="monitor-table-wrap">
      <table className="table monitor-table">
        <thead>
          <tr>
            <th>Agent</th>
            <th>Task</th>
            <th>Status</th>
            <th>Doing</th>
            <th className="num">Time</th>
            <th className="num">Cost</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => {
            const env = r.environment_id ? envs.find((e) => e.id === r.environment_id) : envs.find((e) => e.task?.id === r.task.id && e.phase !== "terminated");
            return (
              <tr key={r.id}>
                <td className="nowrap">
                  <div>{agentName(r.agent_type)}</div>
                  <div className="muted small">
                    {KIND_LABEL[r.kind] ?? r.kind}
                    {r.resumed && " · resumed"}
                  </div>
                </td>
                <td className="monitor-task-cell">
                  <Link to={`/p/${r.project_id}/tasks/${r.task.id}`}>
                    <span className="muted">#{r.task.number}</span> {r.task.title}
                  </Link>
                  <div className="muted small">{r.project_name}</div>
                </td>
                <td>
                  <Pill color={r.status === "queued" ? "gray" : r.status === "starting" ? "yellow" : "blue"}>{r.status}</Pill>
                </td>
                <td className="monitor-activity">
                  <div className="truncate" title={r.activity ?? undefined}>
                    {r.activity ?? <span className="muted">—</span>}
                  </div>
                  <div className="muted small">
                    {r.activity_at ? <RelativeTime iso={r.activity_at} /> : null}
                    {r.events > 0 && <> · {r.events} events</>}
                    {env && (
                      <>
                        {" · "}
                        <button className="link-button" onClick={() => onSelectEnv(env.id)}>
                          environment
                        </button>
                      </>
                    )}
                  </div>
                </td>
                <td className="num nowrap" title={r.started_at ? `Started ${new Date(r.started_at).toLocaleString()}` : `Queued ${new Date(r.created_at).toLocaleString()}`}>
                  {elapsed(r.started_at ?? r.created_at, now)}
                </td>
                <td className="num nowrap">
                  {formatUsd(r.cost_usd)}
                  {r.tokens > 0 && <div className="muted small">{(r.tokens / 1000).toFixed(r.tokens < 10_000 ? 1 : 0)}k tok</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PhaseStepper({ env }: { env: MonitorEnv }) {
  const idx = stepIndex(env);
  if (idx === null) return null;
  return (
    <ol className="phase-steps" aria-label="Spin-up progress">
      {STEPS.map((s, i) => (
        <li key={s} className={cx("phase-step", i < idx && "done", i === idx && (env.phase === "setup_failed" ? "error" : "current"))}>
          <span className="phase-dot" aria-hidden="true">
            {i < idx ? <Icon name="check" size={10} /> : i === idx && env.phase === "setup_failed" ? <Icon name="x" size={10} /> : null}
          </span>
          <span className="phase-label">{s}</span>
        </li>
      ))}
    </ol>
  );
}

function EnvCard({ env, now, selected, onSelect }: { env: MonitorEnv; now: number; selected: boolean; onSelect: () => void }) {
  const meta = PHASE_META[env.phase];
  const startingUp = ["provisioning", "booting", "preparing"].includes(env.phase);
  return (
    <button className={cx("monitor-env", selected && "selected", (env.phase === "failed" || env.phase === "setup_failed") && "failed")} onClick={onSelect}>
      <div className="monitor-env-top">
        <Pill color={meta.color}>{meta.label}</Pill>
        <span className="monitor-env-title truncate">
          {env.task ? (
            <>
              <span className="muted">#{env.task.number}</span> {env.task.title}
            </>
          ) : (
            <span className="muted">Warm pool</span>
          )}
        </span>
        {env.error_count > 0 && (
          <span className="monitor-errors" title={`${env.error_count} error line${env.error_count === 1 ? "" : "s"} in the log`}>
            <Icon name="alert" size={13} /> {env.error_count}
          </span>
        )}
        <span className="muted small nowrap" title={`Created ${new Date(env.created_at).toLocaleString()}`}>
          {startingUp ? elapsed(env.created_at, now) : <RelativeTime iso={env.terminated_at ?? env.created_at} />}
        </span>
      </div>
      <div className="muted small monitor-env-sub">
        {env.project_name} · {env.template_name} · {BACKEND_LABEL[env.backend] ?? env.backend} {env.os}
      </div>
      <PhaseStepper env={env} />
      <div className={cx("monitor-env-phase small", `log-${env.last_log?.level ?? "info"}`)}>
        <span className="monitor-env-label">{env.phase_label}</span>
        {env.last_log && !env.phase_label.includes(env.last_log.message) && (
          <span className="monitor-env-last truncate" title={env.last_log.message}>
            {env.last_log.message}
          </span>
        )}
      </div>
    </button>
  );
}

function EnvDetail({ env, now }: { env: MonitorEnv; now: number }) {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const action = useMutation({
    mutationFn: (a: "stop" | "start" | "terminate") => api.envAction(env.id, a),
    onSettled: () => qc.invalidateQueries({ queryKey: ["monitor"] }),
  });
  const live = !["terminated", "failed"].includes(env.state);
  return (
    <div className="monitor-log-panel">
      <div className="monitor-detail-head">
        <div className="monitor-detail-main">
          <div className="monitor-detail-title">
            {env.task ? (
              <Link to={`/p/${env.project_id}/tasks/${env.task.id}`}>
                #{env.task.number} {env.task.title}
              </Link>
            ) : (
              "Warm-pool environment"
            )}
            {env.task && <StatusPill status={env.task.status} />}
          </div>
          <dl className="monitor-facts small">
            <dt>Phase</dt>
            <dd>{env.phase_label}</dd>
            <dt>Backend</dt>
            <dd>
              {BACKEND_LABEL[env.backend] ?? env.backend} · {env.os} · {env.template_name}
            </dd>
            {env.provider_ref && (
              <>
                <dt>Resource</dt>
                <dd>
                  <code className="monitor-ref">{env.provider_ref}</code>
                </dd>
              </>
            )}
            <dt>Created</dt>
            <dd>
              <RelativeTime iso={env.created_at} /> ({elapsed(env.created_at, now)} ago)
            </dd>
            <dt>Agent link</dt>
            <dd>{env.connected ? "connected" : env.last_seen_at ? <>last seen <RelativeTime iso={env.last_seen_at} /></> : "never connected"}</dd>
            {env.running_minutes >= 1 && (
              <>
                <dt>Run time</dt>
                <dd>{env.running_minutes < 90 ? `${env.running_minutes.toFixed(0)} min` : `${(env.running_minutes / 60).toFixed(1)} h`}</dd>
              </>
            )}
            {env.terminate_after && live && (
              <>
                <dt>Deleted</dt>
                <dd>
                  <RelativeTime iso={env.terminate_after} /> unless used again
                </dd>
              </>
            )}
          </dl>
        </div>
        {me.role === "admin" && live && (
          <div className="monitor-actions">
            {env.state === "stopped" ? (
              <button className="btn btn-sm" disabled={action.isPending} onClick={() => action.mutate("start")}>
                <Icon name="play" size={13} /> Start
              </button>
            ) : (
              <button
                className="btn btn-sm"
                disabled={action.isPending || env.state === "stopping" || env.state === "provisioning"}
                onClick={() => action.mutate("stop")}
                title="Stop the instance; the disk is kept"
              >
                <Icon name="stop" size={13} /> Stop
              </button>
            )}
            <button
              className="btn btn-sm btn-ghost-danger"
              disabled={action.isPending}
              onClick={() => {
                if (confirm("Delete this environment and its disk? Unpushed work in it is lost.")) action.mutate("terminate");
              }}
            >
              <Icon name="trash" size={13} /> Terminate
            </button>
          </div>
        )}
      </div>
      {action.error && <p className="error-text small">{(action.error as Error).message}</p>}
      <EnvLog envId={env.id} live={live} />
    </div>
  );
}

function EnvLog({ envId, live }: { envId: string; live: boolean }) {
  const qc = useQueryClient();
  const key = qk.monitorEnvLogs(envId);
  // Incremental: each fetch asks only for lines after the last one we have.
  const { data: lines = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const prev = qc.getQueryData<EnvLogLine[]>(key) ?? [];
      const fresh = await api.monitorEnvLogs(envId, prev.at(-1)?.id);
      return fresh.length ? [...prev, ...fresh].slice(-5000) : prev;
    },
    refetchInterval: live ? 5_000 : false,
  });
  const [filter, setFilter] = useState("");
  const [problemsOnly, setProblemsOnly] = useState(false);
  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return lines.filter((l) => (!problemsOnly || l.level !== "info") && (!f || l.message.toLowerCase().includes(f)));
  }, [lines, filter, problemsOnly]);

  // Follow the tail unless the reader has scrolled up.
  const box = useRef<HTMLDivElement>(null);
  const follow = useRef(true);
  useLayoutEffect(() => {
    follow.current = true;
  }, [envId]);
  useLayoutEffect(() => {
    if (follow.current && box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [shown.length, envId]);
  const onScroll = () => {
    const el = box.current;
    if (el) follow.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };

  return (
    <div className="env-log">
      <div className="env-log-toolbar">
        <span className="env-log-title">
          <Icon name="terminal" size={14} /> Log
          {live && <span className="live-dot" title="Updates live" />}
        </span>
        <input className="input input-sm" placeholder="Filter" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <label className="small nowrap">
          <input type="checkbox" checked={problemsOnly} onChange={(e) => setProblemsOnly(e.target.checked)} /> Warnings &amp; errors
        </label>
      </div>
      <div className="env-log-lines" ref={box} onScroll={onScroll} role="log" aria-live="polite">
        {isLoading ? (
          <div className="muted">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="muted">{lines.length ? "No lines match." : "Nothing logged yet."}</div>
        ) : (
          shown.map((l) => (
            <div key={l.id} className={cx("env-log-line", `log-${l.level}`)}>
              <time dateTime={l.ts} title={new Date(l.ts).toLocaleString()}>
                {clock(l.ts)}
              </time>
              <span className="env-log-msg">{l.message}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
